// ============================================================
// ColonieDailyReportModal.js
// Daily collections report — print matches payment module pattern
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { X, BarChart2, Printer, Loader } from 'lucide-react';
import { PKG_LABEL } from './ColoniePrintUtils';

const ColonieDailyReportModal = ({ onClose, apiCall }) => {
  const [date, setDate]     = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [loading, setLd]    = useState(false);

  const load = useCallback(async () => {
    setLd(true);
    try { setReport(await apiCall(`/colonie/reports/daily?date=${date}`)); }
    catch (_) { setReport(null); }
    finally { setLd(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [date]);

  const handlePrint = () => {
    if (!report?.payments?.length) return;

    const s        = report.summary;
    const payments = report.payments;
    const fmtDate  = new Date(date + 'T00:00:00').toLocaleDateString('en-GB', {
      day: '2-digit', month: 'long', year: 'numeric'
    });

    const grandTotal = payments.reduce((sum, p) => sum + parseFloat(p.Amount || 0), 0);

    // Package breakdown
    const byPkg = {};
    payments.forEach(p => {
      const k = PKG_LABEL[p.PackageType] || p.PackageType || 'Unknown';
      byPkg[k] = (byPkg[k] || 0) + parseFloat(p.Amount || 0);
    });

    const paymentsTableHTML = `
      <table style="width:100%;border-collapse:collapse;margin-bottom:8px;">
        <thead>
          <tr style="background-color:#fff7ed;border-bottom:2px solid #f97316;">
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#374151;">Receipt #</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#374151;">Child</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#374151;">Phone</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#374151;">Package</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;font-weight:600;color:#374151;">Method</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#374151;">Charged</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#374151;">Paid Today</th>
            <th style="padding:8px 10px;text-align:right;font-size:12px;font-weight:600;color:#374151;">Balance</th>
          </tr>
        </thead>
        <tbody>
          ${payments.map(p => `
            <tr style="border-bottom:1px solid #f0f0f0;">
              <td style="padding:6px 10px;font-size:11px;color:#111;font-family:monospace;">${p.ReceiptNumber}</td>
              <td style="padding:6px 10px;font-size:12px;font-weight:600;color:#111;">${p.ChildName}</td>
              <td style="padding:6px 10px;font-size:11px;color:#555;">${p.MotherPhone || ''}</td>
              <td style="padding:6px 10px;font-size:11px;color:#333;">${PKG_LABEL[p.PackageType] || p.PackageType || '—'}</td>
              <td style="padding:6px 10px;font-size:11px;color:#333;">${p.PaymentMethod}</td>
              <td style="padding:6px 10px;font-size:11px;text-align:right;color:#555;">$${parseFloat(p.TotalCharged || 0).toFixed(2)}</td>
              <td style="padding:6px 10px;font-size:12px;text-align:right;font-weight:700;color:#16a34a;">$${parseFloat(p.Amount || 0).toFixed(2)}</td>
              <td style="padding:6px 10px;font-size:12px;text-align:right;font-weight:600;color:${parseFloat(p.BalanceRemaining || 0) > 0 ? '#dc2626' : '#16a34a'};">$${parseFloat(p.BalanceRemaining || 0).toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr style="background-color:#fff7ed;border-top:2px solid #f97316;">
            <td colspan="6" style="padding:8px 10px;text-align:right;font-weight:700;font-size:13px;color:#374151;">Total Collected:</td>
            <td style="padding:8px 10px;text-align:right;font-weight:700;font-size:14px;color:#ea580c;">$${grandTotal.toFixed(2)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>`;

    const pkgBreakdownHTML = `
      <div style="margin-top:16px;padding:12px;background-color:#fff7ed;border:1px solid #fdba74;border-radius:6px;">
        <h4 style="font-size:12px;font-weight:700;color:#9a3412;margin:0 0 8px 0;">Package Breakdown:</h4>
        <ul style="list-style:none;padding:0;margin:0;">
          ${Object.entries(byPkg).map(([pkg, amt]) => `
            <li style="font-size:12px;padding:4px 0;display:flex;justify-content:space-between;border-bottom:1px dotted #fdba74;">
              <span style="color:#78350f;">${pkg}</span>
              <span style="font-weight:700;color:#9a3412;">$${amt.toFixed(2)}</span>
            </li>
          `).join('')}
        </ul>
      </div>`;

    const summaryBoxHTML = `
      <div style="margin-top:24px;padding:20px;background-color:#f3f4f6;border:2px solid #9ca3af;border-radius:8px;">
        <h3 style="font-size:16px;font-weight:700;text-align:center;margin:0 0 14px 0;color:#111827;">Daily Summary — ${fmtDate}</h3>
        <div style="display:flex;justify-content:space-between;font-size:13px;padding:5px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Total Transactions:</span>
          <span style="font-weight:700;">${payments.length}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:13px;padding:5px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Cash:</span>
          <span style="font-weight:700;color:#15803d;">$${parseFloat(s?.CashTotal || 0).toFixed(2)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:13px;padding:5px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Check:</span>
          <span style="font-weight:700;color:#1d4ed8;">$${parseFloat(s?.CheckTotal || 0).toFixed(2)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:13px;padding:5px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">OMT:</span>
          <span style="font-weight:700;color:#7c3aed;">$${parseFloat(s?.OmtTotal || 0).toFixed(2)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:13px;padding:5px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Whish:</span>
          <span style="font-weight:700;color:#be185d;">$${parseFloat(s?.WhishTotal || 0).toFixed(2)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:17px;font-weight:700;padding:10px 0 0 0;margin-top:6px;border-top:2px solid #6b7280;">
          <span style="color:#111827;">GRAND TOTAL:</span>
          <span style="color:#111827;">$${grandTotal.toFixed(2)}</span>
        </div>
      </div>`;

    // ── Open print window (exact same pattern as your payment module) ──
    const windowPrint = window.open('', '', 'width=900,height=700');
    windowPrint.document.write(`
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Colonie Daily Report — ${fmtDate}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #1f2937; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div style="text-align:center;margin-bottom:24px;">
            <h1 style="font-size:24px;margin:0 0 6px 0;color:#111827;">Collège des Apôtres</h1>
            <h2 style="font-size:16px;margin:0 0 4px 0;color:#f97316;">Colonie Des Rêves — Summer Camp 2026</h2>
            <p style="font-size:13px;color:#6b7280;margin:0;">Daily Collections — ${fmtDate}</p>
          </div>
          <div style="margin-bottom:24px;">
            <h3 style="font-size:15px;font-weight:700;margin:0 0 10px 0;background-color:#fff7ed;padding:8px 14px;border-radius:4px;color:#9a3412;">
              Colonie Payments
            </h3>
            ${payments.length > 0 ? paymentsTableHTML + pkgBreakdownHTML : '<p style="text-align:center;padding:20px;color:#6b7280;font-style:italic;">No payments for this date</p>'}
          </div>
          ${summaryBoxHTML}
        </body>
      </html>
    `);
    windowPrint.document.close();
    windowPrint.focus();
    windowPrint.print();
    windowPrint.close();
  };

  const s = report?.summary;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <BarChart2 size={19}/>Daily Report — Colonie
          </h2>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>

        <div className="p-5 flex-1 overflow-auto">
          <div className="flex gap-3 mb-5">
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-orange-400"/>
            <button onClick={handlePrint}
              disabled={!report?.payments?.length}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 disabled:opacity-50">
              <Printer size={15}/>Print
            </button>
          </div>

          {loading && <div className="text-center py-10"><Loader size={26} className="animate-spin text-orange-500 mx-auto"/></div>}

          {s && !loading && (
            <div className="grid grid-cols-6 gap-3 mb-5">
              {[
                ['Total',    `$${parseFloat(s.TotalCollected || 0).toFixed(2)}`, 'text-orange-600'],
                ['Receipts',  s.TotalTransactions,                               'text-gray-700'],
                ['Cash',     `$${parseFloat(s.CashTotal  || 0).toFixed(2)}`,    'text-green-600'],
                ['Check',    `$${parseFloat(s.CheckTotal || 0).toFixed(2)}`,    'text-blue-600'],
                ['OMT',      `$${parseFloat(s.OmtTotal   || 0).toFixed(2)}`,    'text-purple-600'],
                ['Whish',    `$${parseFloat(s.WhishTotal || 0).toFixed(2)}`,    'text-pink-600'],
              ].map(([l, v, c]) => (
                <div key={l} className="bg-gray-50 border rounded-xl p-3 text-center">
                  <div className={`text-lg font-bold ${c}`}>{v}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{l}</div>
                </div>
              ))}
            </div>
          )}

          {!loading && report?.payments?.length > 0 ? (
            <>
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="bg-orange-50 text-orange-800 text-xs">
                    {['Receipt','Child','Phone','Package','Total','Paid Today','Balance','Method'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.payments.map(p => (
                    <tr key={p.PaymentID} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{p.ReceiptNumber}</td>
                      <td className="px-3 py-2 font-semibold">{p.ChildName}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{p.MotherPhone}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{PKG_LABEL[p.PackageType] || p.PackageType}</td>
                      <td className="px-3 py-2 text-gray-600">${parseFloat(p.TotalCharged || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 font-bold text-green-700">${parseFloat(p.Amount).toFixed(2)}</td>
                      <td className={`px-3 py-2 font-semibold ${parseFloat(p.BalanceRemaining) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ${parseFloat(p.BalanceRemaining || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2">{p.PaymentMethod}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Package breakdown shown on screen too */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <h4 className="text-sm font-bold text-orange-800 mb-3">Package Breakdown</h4>
                <div className="space-y-1">
                  {Object.entries(
                    report.payments.reduce((acc, p) => {
                      const k = PKG_LABEL[p.PackageType] || p.PackageType || 'Unknown';
                      acc[k] = (acc[k] || 0) + parseFloat(p.Amount || 0);
                      return acc;
                    }, {})
                  ).map(([pkg, amt]) => (
                    <div key={pkg} className="flex justify-between text-sm border-b border-orange-100 pb-1">
                      <span className="text-gray-700">{pkg}</span>
                      <span className="font-bold text-orange-800">${amt.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (!loading && <p className="text-center text-gray-400 py-8">No payments for this date.</p>)}
        </div>
      </div>
    </div>
  );
};

export default ColonieDailyReportModal;
